'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { isAxiosError } from 'axios'
import { ArrowDown, ArrowUp, ArrowLeft, FileDown } from 'lucide-react'
import { toast } from 'sonner'

import { getJourneyAccessState } from '../../domain/getJourneyAccessState'
import { useActiveJourney } from '../../hooks/useActiveJourney'
import { useDynamicBackgroundPattern } from '../../hooks/useDynamicBackgroundPattern'
import { fetchJourneyAnswersExport } from '../../services/journey-answer-export.api'
import { generateJourneyAnswersPdf } from '../../services/journey-answer-export.pdf'
import { useJourneysStore } from '../../stores/useJourneysStore'
import { BackgroundPatternColumn } from '../BackgroundPattern'
import { JourneysList } from '../JourneysList'
import { LevelCheckPoint } from '../LevelCheckPoint'

import type { Journey } from '@/features/journeys'
import { buildPageTitle } from '@/shared/lib/page-title'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { sortByPosition } from '@/shared/lib/sort'
import { Page } from '@/shared/ui/Page'
import { PointsScoreboard } from '@/shared/ui/PointsScoreboard'
import { UnavailableState } from '@/shared/ui/UnavailableState'


type CurrentStage = {
  journeyId: number
  journeyName: string
  stageId: number
  stageName: string
} | null

export function JourneysListPage() {
  const [currentStage, setCurrentStage] = useState<CurrentStage>(null)
  const [isCurrentLevelVisible, setIsCurrentLevelVisible] = useState(true)
  const [offscreenDirection, setOffscreenDirection] = useState<
    'up' | 'down' | null
  >(null)
  const [showJourneyList, setShowJourneyList] = useState(false)
  const stageRefs = useRef(
    new Map<
      string,
      {
        el: HTMLElement | null
        journey: Journey
        stage: { id: number; name: string }
      }
    >()
  )
  const headerRef = useRef<HTMLDivElement | null>(null)
  const hasAutoScrolledRef = useRef(false)
  const contentContainerRef = useRef<HTMLDivElement | null>(null)

  const { journeys, getCurrentLevel } = useJourneysStore()
  const { progress } = useUserProgress()
  const { activeJourneyId, setActiveJourneyId, clearActiveJourney } =
    useActiveJourney()
  const [isDownloadingJourneyPdf, setIsDownloadingJourneyPdf] = useState(false)

  const currentJourney = activeJourneyId
    ? journeys.find(j => j.id === activeJourneyId)
    : null
  const journeyAccessState = getJourneyAccessState({
    currentJourney: currentJourney ?? null,
    lockState: currentJourney
      ? {
          blocked: currentJourney._progress.blocked,
          blockedByJourneyName: currentJourney._progress.blockedByJourneyName
        }
      : undefined
  })

  const stages = [...(currentJourney?.active_stages || [])].sort(sortByPosition)

  const totalLevels = useMemo(() => {
    return stages.reduce((total, stage) => {
      return total + (stage.active_levels?.length || 0)
    }, 0)
  }, [stages])

  const backgroundPatternConfig = useMemo(
    () => ({
      leftImages: [
        {
          src: '/bgs/left-1.svg',
          height: 500,
          className: 'h-[500px] object-cover object-right'
        },
        {
          src: '/bgs/left-2.svg',
          height: 250,
          className: 'h-[250px] object-contain object-left -ml-5'
        }
      ],
      rightImages: [
        {
          src: '/bgs/right-1.svg',
          height: 650,
          className: 'h-[650px] object-cover object-left'
        },
        {
          src: '/bgs/right-2.svg',
          height: 650,
          className: 'h-[650px] mt-[100px] object-cover object-left'
        }
      ],
      spacing: {
        left: { mobile: 210, tablet: 200, desktop: 200 },
        right: { mobile: 520, tablet: 520, desktop: 520 }
      },
      minColumns: 2
    }),
    []
  )

  const { leftColumns, rightColumns } = useDynamicBackgroundPattern({
    contentRef: contentContainerRef,
    config: backgroundPatternConfig,
    totalLevels
  })

  useEffect(() => {
    if (journeys.length === 0) return
    if (showJourneyList === true) return
    if (!activeJourneyId) {
      setShowJourneyList(true)
      return
    }
    const journeyExists = journeys.some(j => j.id === activeJourneyId)
    if (!journeyExists) {
      setShowJourneyList(true)
      return
    }
    setShowJourneyList(false)
  }, [journeys, activeJourneyId, showJourneyList])

  useEffect(() => {
    document.title = buildPageTitle(
      'Jornadas',
      !showJourneyList ? currentJourney?.name : undefined
    )
  }, [currentJourney, showJourneyList])

  const handleSelectJourney = (journey: Journey) => {
    setActiveJourneyId(journey.id)
    setShowJourneyList(false)
    hasAutoScrolledRef.current = false
    setCurrentStage(null)
  }

  const handleBackToList = () => {
    clearActiveJourney()
    setShowJourneyList(true)
  }

  const currentLevelData = getCurrentLevel(currentJourney?.id)
  const currentLevelUid = currentLevelData?.level.uid

  const promoteStage = (id: string) => {
    const data = stageRefs.current.get(id)
    if (!data) return
    const next = {
      journeyId: data.journey.id,
      journeyName: data.journey.name,
      stageId: data.stage.id,
      stageName: data.stage.name
    }
    setCurrentStage(prev =>
      prev?.stageId === next.stageId && prev?.journeyId === next.journeyId
        ? prev
        : next
    )
  }

  useEffect(() => {
    if (!currentJourney || stages.length === 0) return

    const headerH = headerRef.current?.offsetHeight ?? 0
    const THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20)
    const visibleRatios = new Map<string, number>()

    let rafId: number | null = null
    const scheduleChooseBest = () => {
      if (rafId != null) return
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        let bestId: string | null = null
        let bestRatio = -1
        visibleRatios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio
            bestId = id
          }
        })
        if (bestId) promoteStage(bestId)
      })
    }

    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id
          visibleRatios.set(id, entry.intersectionRatio)
        }
        scheduleChooseBest()
      },
      {
        root: null,
        rootMargin: `${-(headerH + 8)}px 0px -40% 0px`,
        threshold: THRESHOLDS
      }
    )

    for (const { el } of stageRefs.current.values()) {
      if (el) io.observe(el)
    }

    let scrollRaf: number | null = null
    const onScrollOrResize = () => {
      if (scrollRaf != null) return
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = null
        const header = headerRef.current?.offsetHeight ?? 0
        const mid = header + (window.innerHeight - header) / 2
        let bestId: string | null = null
        let bestDist = Infinity
        stageRefs.current.forEach(({ el }, id) => {
          if (!el) return
          const rect = el.getBoundingClientRect()
          const elMid = rect.top + rect.height / 2
          const dist = Math.abs(elMid - mid)
          if (dist < bestDist) {
            bestDist = dist
            bestId = id
          }
        })
        if (bestId) promoteStage(bestId)
      })
    }

    window.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)
    onScrollOrResize()

    return () => {
      io.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
      if (scrollRaf) cancelAnimationFrame(scrollRaf)
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [currentJourney?.id, stages.length])

  useEffect(() => {
    if (!currentLevelUid) return
    const target = document.getElementById(`level-${currentLevelUid}`)
    if (!target) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsCurrentLevelVisible(entry.isIntersecting)
        if (!entry.isIntersecting) {
          if (entry.boundingClientRect.top < 0) setOffscreenDirection('up')
          else if (entry.boundingClientRect.top > window.innerHeight)
            setOffscreenDirection('down')
          else setOffscreenDirection(null)
        } else {
          setOffscreenDirection(null)
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [currentLevelUid])

  const scrollElementToCenteredView = (el: HTMLElement) => {
    const header = headerRef.current?.offsetHeight ?? 0
    const viewportCenter = header + (window.innerHeight - header) / 2
    const rect = el.getBoundingClientRect()
    const targetCenter = rect.top + window.scrollY + rect.height / 2
    const top = Math.max(0, targetCenter - viewportCenter)
    window.scrollTo({ top, behavior: 'smooth' })
  }

  const scrollToCurrentLevel = () => {
    if (!currentLevelUid) return
    const target = document.getElementById(`level-${currentLevelUid}`)
    if (!target) return
    scrollElementToCenteredView(target)
  }

  useEffect(() => {
    if (!currentLevelUid || hasAutoScrolledRef.current) return
    const target = document.getElementById(`level-${currentLevelUid}`)
    if (!target) return
    requestAnimationFrame(() => {
      scrollElementToCenteredView(target)
      hasAutoScrolledRef.current = true
    })
  }, [currentLevelUid])

  useEffect(() => {
    if (!currentStage && currentJourney && stages.length > 0) {
      const firstStage = stages[0]
      if (firstStage) {
        setCurrentStage({
          journeyId: currentJourney.id,
          journeyName: currentJourney.name,
          stageId: firstStage.id,
          stageName: firstStage.name
        })
      }
    }
  }, [currentJourney, stages, currentStage])

  if (showJourneyList || !currentJourney) {
    return <JourneysList onSelectJourney={handleSelectJourney} />
  }

  if (journeyAccessState.kind !== 'ready') {
    return (
      <Page className="bg-[#378bea] px-0 lg:px-0">
        <UnavailableState
          title={journeyAccessState.title}
          description={journeyAccessState.description}
          onAction={handleBackToList}
        />
      </Page>
    )
  }

  let index = -1

  const completedLevelsInJourney =
    currentJourney?.active_stages
      ?.flatMap(stage => stage.active_levels ?? [])
      .filter(level => progress?.finished_levels?.includes(level.id)).length ?? 0

  const canDownloadJourneyPdf = completedLevelsInJourney > 0

  const handleDownloadJourneyAnswers = async () => {
    if (!currentJourney || !canDownloadJourneyPdf || isDownloadingJourneyPdf) return

    try {
      setIsDownloadingJourneyPdf(true)
      const data = await fetchJourneyAnswersExport(currentJourney.uid)
      await generateJourneyAnswersPdf(data)
    } catch (error) {
      if (isAxiosError<{ error?: string }>(error)) {
        toast.error(
          error.response?.data?.error ||
            'Nao foi possivel gerar o PDF consolidado da jornada.'
        )
        return
      }

      toast.error('Nao foi possivel gerar o PDF consolidado da jornada.')
    } finally {
      setIsDownloadingJourneyPdf(false)
    }
  }

  return (
    <Page className="bg-[#378bea] px-0 lg:px-0">
      <div
        ref={headerRef}
        className="sticky left-0 z-40 flex items-start justify-center w-full lg:mt-10 lg:mb-10 top-18 lg:top-4 h-[135px] px-4"
      >
        <div className="overflow-hidden bg-white rounded-xl w-xl min-h-15 z-2 border-b-6 border-secondary">
          <div className="relative w-full px-4 py-1.5 font-bold bg-primary-foreground text-dark text-base lg:text-md pl-14 lg:pl-16 pr-1.5 lg:pr-2">
            <button
              type="button"
              onClick={handleBackToList}
              aria-label="Voltar para listagem de jornadas"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 size-8 lg:size-10 flex items-center justify-center rounded-sm bg-primary hover:bg-primary/90 transition-colors text-white shadow-md"
            >
              <ArrowLeft className="size-4 lg:size-5" />
            </button>
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-center flex-1">
                {currentStage?.journeyName}
              </span>
              {canDownloadJourneyPdf ? (
                <button
                  type="button"
                  aria-label="Baixar respostas da jornada em PDF"
                  title="Baixar respostas"
                  onClick={handleDownloadJourneyAnswers}
                  disabled={isDownloadingJourneyPdf}
                  className="size-7 lg:size-8 shrink-0 flex items-center justify-center rounded-sm bg-primary text-white transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                >
                  <FileDown className="size-3.5 lg:size-4" />
                </button>
              ) : null}
            </div>
          </div>
          <div className="px-2 py-2 lg:py-3 flex flex-col items-center gap-1">
            <span className="block text-md lg:text-xl font-bold text-center uppercase text-secondary">
              {currentStage?.stageName}
            </span>
            <PointsScoreboard
              size="lg"
              acquiredPoints={currentJourney._progress.rewards.acquiredPoints}
              totalPoints={currentJourney._progress.rewards.totalPoints}
            />
          </div>
        </div>
      </div>

      <div
        ref={contentContainerRef}
        className="flex flex-col mt-10 w-full relative"
      >
        {stages.map(stage => {
          const levels = (stage.active_levels ?? [])
            .slice()
            .sort(sortByPosition)
          const stageId = `stage-${currentJourney.id}-${stage.id}`

          return (
            <div
              key={stage.id}
              id={stageId}
              className="relative flex flex-col items-center"
              ref={el => {
                stageRefs.current.set(stageId, {
                  el,
                  journey: currentJourney,
                  stage
                })
              }}
            >
              {levels.map(level => {
                index++
                return (
                  <LevelCheckPoint
                    key={level.id}
                    stage={stage}
                    level={level}
                    index={index}
                    journeyUid={currentJourney.uid}
                  />
                )
              })}
            </div>
          )
        })}
        <div className="h-full overflow-hidden absolute -top-25 left-0 w-full z-0 pointer-events-none select-none">
          <div className="block w-40 md:w-60 lg:w-90 h-full absolute left-0 top-50 z-0 pointer-events-none overflow-hidden">
            {leftColumns.map(column => (
              <BackgroundPatternColumn
                key={column.id}
                images={[
                  {
                    src: '/bgs/left-1.svg',
                    className: 'h-[500px] object-cover object-right'
                  },
                  {
                    src: '/bgs/left-2.svg',
                    className: 'h-[250px] object-contain object-left -ml-5'
                  },
                  {
                    src: '/bgs/left-1.svg',
                    className: 'h-[500px] object-cover object-right mt-[200px]'
                  },
                  {
                    src: '/bgs/left-2.svg',
                    className: 'h-[250px] object-contain object-left -ml-5'
                  }
                ]}
                direction="left"
                offset={column.offset}
              />
            ))}
          </div>

          <div className="block w-40 md:w-60 lg:w-90 h-full absolute right-0 top-0 z-0 pointer-events-none">
            {rightColumns.map(column => (
              <BackgroundPatternColumn
                key={column.id}
                images={[
                  {
                    src: '/bgs/right-1.svg',
                    className: 'h-[650px] object-cover object-left'
                  },
                  {
                    src: '/bgs/right-2.svg',
                    className: 'h-[650px] object-cover object-left'
                  }
                ]}
                direction="right"
                offset={column.offset}
              />
            ))}
          </div>
        </div>
        {currentLevelUid && (
          <button
            type="button"
            aria-label="Ir para o nível atual"
            onClick={scrollToCurrentLevel}
            className={`group sticky bottom-22 lg:bottom-6 ml-auto mr-4 lg:mr-6 z-50 size-9 lg:size-12 items-center flex justify-center rounded-sm px-2 py-2 font-semibold transition-all duration-100 select-none focus:outline-none bg-linear-to-b from-white to-white shadow-[inset_0_3px_0_0_#FFFFFF,0_4px_0px_#C9D6EE]  hover:translate-y-0.5 hover:shadow-[inset_0_3px_0_0_#fff,0_2px_0px_#C9D6EE] active:translate-y-1 active:shadow-[inset_0_3px_0_0_#fff] text-primary ${
              isCurrentLevelVisible ? 'hidden' : ''
            }`}
          >
            {offscreenDirection === 'up' ? (
              <ArrowUp className="h-6 w-6" />
            ) : (
              <ArrowDown className="h-6 w-6" />
            )}
          </button>
        )}
      </div>

      <div
        className="absolute top-0 left-0 w-full h-full shadow-inner pointer-events-none select-none hidden md:block"
        style={{
          boxShadow: 'inset 0 2px 4px 0 #0d0101'
        }}
      />

    </Page>
  )
}
